import { type ReactNode, useEffect, useMemo, useState } from "react";
import { billingService } from "../../../Api/Services/billingService";
import { reportService } from "../../../Api/Services/reportService";
import {
  BillingChartPoint,
  BillingOrder,
  BillingPaymentMethodBreakdown,
  BillingReportResponse,
} from "../../../Models/Billing";
import { OrderSource } from "../../../Enums/OrderSource";
import { ReportFormat } from "../../../Enums/ReportFormat";
import {
  getPaymentMethodLabel,
  getPaymentStatusLabel,
} from "../../../Utils/Enums";
import {
  formatDateTimeToBR,
  formatPriceBRL,
} from "../../../Utils/DataFormatters";
import {
  FiCalendar,
  FiDollarSign,
  FiPieChart,
  FiTrendingUp,
  FiTruck,
} from "react-icons/fi";
import { FaInfoCircle, FaBoxOpen } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import PageTitle from "../../../Components/PageTitle/PageTitle";
import BillingFeeDetailsModal from "../../../Components/BillingFeeDetailsModal/BillingFeeDetailsModal";
import ManageSubscriptionCard from "../../../Components/ManageSubscriptionCard/ManageSubscriptionCard";
import ReportFormatModal from "../../../Components/ReportFormatModal/ReportFormatModal";
import Button from "../../../Components/Button/Button";

const defaultDate = (daysAgo: number) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().split("T")[0];
};

const StatCard = ({
  title,
  value,
  icon,
  accent,
  isLoading,
}: {
  title: string;
  value: string;
  icon: ReactNode;
  accent: string;
  isLoading?: boolean;
}) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex items-center justify-between hover:shadow-md transition">
    <div>
      <p className="text-sm text-gray-500">{title}</p>
      {isLoading ? (
        <div className="h-7 w-28 bg-gray-100 rounded animate-pulse mt-1" />
      ) : (
        <p className="text-xl sm:text-2xl font-semibold mt-1">{value}</p>
      )}
    </div>
    <div className={`p-3 rounded-full ${accent} text-white`}>
      {isLoading ? (
        <div className="h-5 w-5 bg-white/40 rounded-full animate-pulse" />
      ) : (
        icon
      )}
    </div>
  </div>
);

const DailyLineChart = ({
  points,
  isLoading,
}: {
  points: BillingChartPoint[];
  isLoading?: boolean;
}) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="h-56 bg-white border border-gray-200 rounded-xl p-4 shadow-sm animate-pulse">
        <div className="flex items-center justify-between mb-3">
          <div className="h-4 w-40 bg-gray-100 rounded" />
          <div className="h-3 w-24 bg-gray-100 rounded" />
        </div>
        <div className="h-40 bg-gray-50 rounded" />
      </div>
    );
  }

  if (!points.length) {
    return (
      <div className="flex items-center justify-center h-56 bg-white border border-dashed border-gray-200 rounded-xl text-gray-500">
        {"Nenhum pagamento no período selecionado."}
      </div>
    );
  }

  const width = 700;
  const height = 240;
  const padding = { top: 12, right: 20, bottom: 32, left: 56 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const maxValue = Math.max(...points.map((p) => p.gross), 1);
  const tickCount = 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
  const niceMax =
    Math.ceil(maxValue / magnitude / tickCount) * magnitude * tickCount || 1;
  const step = points.length > 1 ? plotWidth / (points.length - 1) : plotWidth;

  const formatCompactBRL = (value: number) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1).replace(".", ",")}M`;
    }
    if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(1).replace(".", ",")}k`;
    }
    return formatPriceBRL(value);
  };

  const getPointPosition = (idx: number, value: number) => {
    const x = padding.left + idx * step;
    const y = padding.top + (1 - value / niceMax) * plotHeight;
    return { x, y };
  };

  const path = points
    .map((p, idx) => {
      const { x, y } = getPointPosition(idx, p.gross);
      return `${idx === 0 ? "M" : "L"}${x},${y}`;
    })
    .join(" ");

  const areaPath = `${path} L ${padding.left + plotWidth} ${padding.top + plotHeight} L ${padding.left} ${
    padding.top + plotHeight
  } Z`;

  const xLabelStep = Math.max(1, Math.floor(points.length / 6));
  const formatMonthLabel = (
    dateStr: string,
    yearStyle: "2-digit" | "numeric",
  ) =>
    new Date(dateStr).toLocaleDateString("pt-BR", {
      month: "short",
      year: yearStyle,
      timeZone: "UTC",
    });

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-gray-700">
          {"Faturamento por mês"}
        </p>
        <p className="text-xs text-gray-500">
          {"Total bruto"}
        </p>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-60">
        <defs>
          <linearGradient id="lineFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#2563eb" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
          </linearGradient>
        </defs>
        <rect
          x={padding.left}
          y={padding.top}
          width={plotWidth}
          height={plotHeight}
          fill="transparent"
        />

        {Array.from({ length: tickCount + 1 }).map((_, idx) => {
          const value = niceMax - (niceMax / tickCount) * idx;
          const y = padding.top + (plotHeight / tickCount) * idx;
          return (
            <g key={`y-${value}`}>
              <line
                x1={padding.left}
                x2={padding.left + plotWidth}
                y1={y}
                y2={y}
                stroke="#e5e7eb"
              />
              <text
                x={padding.left - 8}
                y={y + 4}
                textAnchor="end"
                className="text-[10px]"
                fill="#6b7280"
              >
                {formatCompactBRL(value)}
              </text>
            </g>
          );
        })}

        <line
          x1={padding.left}
          x2={padding.left}
          y1={padding.top}
          y2={padding.top + plotHeight}
          stroke="#d1d5db"
        />
        <line
          x1={padding.left}
          x2={padding.left + plotWidth}
          y1={padding.top + plotHeight}
          y2={padding.top + plotHeight}
          stroke="#d1d5db"
        />

        <path d={areaPath} fill="url(#lineFill)" />
        <path
          d={path}
          fill="none"
          stroke="#2563eb"
          strokeWidth="3"
          strokeLinecap="round"
        />

        {hoverIndex !== null &&
          points[hoverIndex] &&
          (() => {
            const point = points[hoverIndex];
            const { x, y } = getPointPosition(hoverIndex, point.gross);
            const tooltipWidth = 150;
            const tooltipHeight = 44;
            const tooltipX = Math.min(
              Math.max(x - tooltipWidth / 2, padding.left),
              padding.left + plotWidth - tooltipWidth,
            );
            const tooltipY = Math.max(y - tooltipHeight - 10, padding.top);
            return (
              <g>
                <line
                  x1={x}
                  x2={x}
                  y1={padding.top}
                  y2={padding.top + plotHeight}
                  stroke="#cbd5f5"
                  strokeDasharray="4 4"
                />
                <circle cx={x} cy={y} r={5} fill="#2563eb" />
                <rect
                  x={tooltipX}
                  y={tooltipY}
                  width={tooltipWidth}
                  height={tooltipHeight}
                  rx={8}
                  fill="#111827"
                  opacity="0.9"
                />
                <text
                  x={tooltipX + 10}
                  y={tooltipY + 18}
                  className="text-[10px]"
                  fill="#f9fafb"
                >
                  {formatMonthLabel(point.date, "numeric")}
                </text>
                <text
                  x={tooltipX + 10}
                  y={tooltipY + 34}
                  className="text-[11px]"
                  fill="#f9fafb"
                >
                  {"Total bruto"}:{" "}
                  {formatPriceBRL(point.gross)}
                </text>
              </g>
            );
          })()}

        {points.map((p, idx) => {
          const { x, y } = getPointPosition(idx, p.gross);
          const labelDate = formatMonthLabel(p.date, "2-digit");
          const showLabel = idx % xLabelStep === 0 || idx === points.length - 1;
          return (
            <g key={p.date}>
              <circle
                cx={x}
                cy={y}
                r={4}
                fill="#2563eb"
                onMouseEnter={() => setHoverIndex(idx)}
                onMouseLeave={() => setHoverIndex(null)}
              />
              <circle
                cx={x}
                cy={y}
                r={10}
                fill="transparent"
                onMouseEnter={() => setHoverIndex(idx)}
                onMouseLeave={() => setHoverIndex(null)}
              />
              {showLabel && (
                <text
                  x={x}
                  y={padding.top + plotHeight + 18}
                  textAnchor="middle"
                  className="text-[10px]"
                  fill="#4b5563"
                >
                  {labelDate}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const PaymentMethodBars = ({
  methods,
  isLoading,
}: {
  methods: BillingPaymentMethodBreakdown[];
  isLoading?: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm h-full animate-pulse">
        <div className="flex items-center justify-between mb-3">
          <div className="h-4 w-36 bg-gray-100 rounded" />
          <div className="h-3 w-24 bg-gray-100 rounded" />
        </div>
        <div className="space-y-3">
          {[...Array(3)].map((_, idx) => (
            <div key={idx} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="h-3 w-28 bg-gray-100 rounded" />
                <div className="h-3 w-16 bg-gray-100 rounded" />
              </div>
              <div className="h-3 bg-gray-100 rounded-full" />
              <div className="h-3 w-20 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!methods.length) {
    return (
      <div className="flex items-center justify-center h-full min-h-[180px] text-gray-500 border border-dashed border-gray-200 rounded-xl bg-white">
        {"Nenhuma forma de pagamento finalizada no período."}
      </div>
    );
  }

  const max = Math.max(...methods.map((m) => m.gross), 1);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm h-full">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-gray-700">
          {"Por forma de pagamento"}
        </p>
        <span className="text-xs text-gray-500">
          {"Valores brutos"}
        </span>
      </div>
      <div className="space-y-3">
        {methods.map((method) => (
          <div key={method.method}>
            <div className="flex items-center justify-between text-sm text-gray-700 mb-1">
              <span>{getPaymentMethodLabel(method.method)}</span>
              <span className="text-gray-500 text-xs">
                {method.count}{" "}
                {method.count === 1
                  ? "pedido"
                  : "pedidos"}
              </span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-primary-600"
                style={{ width: `${Math.max(8, (method.gross / max) * 100)}%` }}
              />
            </div>
            <div className="text-xs text-gray-600 mt-1">
              {formatPriceBRL(method.gross)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminBillingPage = () => {

  const [report, setReport] = useState<BillingReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [startDate, setStartDate] = useState<string>(defaultDate(30));
  const [endDate, setEndDate] = useState<string>(defaultDate(0));
  const [orderSource, setOrderSource] = useState<OrderSource | undefined>(undefined);
  const [selectedFeeOrder, setSelectedFeeOrder] = useState<BillingOrder | null>(
    null,
  );
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isReportFormatModalOpen, setIsReportFormatModalOpen] = useState(false);
  const pageSize = 10;

  const totalPages = useMemo(() => report?.orders.totalPages ?? 1, [report]);

  const loadReport = async (
    pageToLoad: number = 1,
    showLoading: boolean = true,
    sourceOverride?: OrderSource | null,
  ) => {
    if (showLoading) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }

    const sourceFilter = sourceOverride === undefined ? orderSource : (sourceOverride ?? undefined);

    try {
      const data = await billingService.getReport({
        startDate,
        endDate,
        page: pageToLoad,
        pageSize,
        orderSource: sourceFilter,
      });

      setReport(data);
      setPage(pageToLoad);
    } catch {
      toast.error(
        "Não foi possível carregar o faturamento. Tente novamente.",
      );
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadReport(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast.warn(
        "A data inicial deve ser anterior à data final.",
      );
      return;
    }
    loadReport(1);
  };

  const handleRefresh = () => loadReport(page, false);

  const handleExportReport = async (format: ReportFormat) => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast.warn(
        "A data inicial deve ser anterior à data final.",
      );
      return;
    }

    try {
      setIsGeneratingReport(true);
      const report = await reportService.createBillingReport({
        startDate: startDate || null,
        endDate: endDate || null,
        format,
        orderSource: orderSource ?? null,
      });
      toast.success(
        "Relatório gerado. Abrindo download...",
      );
      window.open(report.url, "_blank", "noopener");
    } catch {
      toast.error(
        "Não foi possível gerar o relatório de faturamento.",
      );
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleOpenReportModal = () => {
    setIsReportFormatModalOpen(true);
  };

  const handleSelectReportFormat = (format: ReportFormat) => {
    setIsReportFormatModalOpen(false);
    handleExportReport(format);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) {
      return;
    }
    loadReport(newPage);
  };

  const orders: BillingOrder[] = report?.orders.items ?? [];

  const getOrderSourceLabel = (source?: OrderSource) => {
    if (source === OrderSource.Online) {
      return "Online";
    }
    if (source === OrderSource.InStore) {
      return "Balcão";
    }
    return "-";
  };

  const getFeePayerLabel = (payer?: number | null) => {
    if (payer === 1) {
      return "Loja pagou";
    }
    if (payer === 2) {
      return "Cliente pagou";
    }
    return "Não informado";
  };

  const getFeeAmount = (order: BillingOrder) => {
    if (order.feeBreakdown?.totalFeeAmount) {
      return order.feeBreakdown.totalFeeAmount;
    }
    return order.paymentFee;
  };

  const getFeePayerSummary = (order: BillingOrder) => {
    if (!order.feeBreakdown) {
      return getFeePayerLabel(order.paymentFeePaidBy);
    }

    const { gatewayFeeAmount, installmentFeeAmount, installmentFeePaidBy } =
      order.feeBreakdown;
    const hasGatewayFee = gatewayFeeAmount > 0;
    const hasInstallmentFee = installmentFeeAmount > 0;

    if (!hasGatewayFee && !hasInstallmentFee) {
      return "";
    }

    if (hasGatewayFee && hasInstallmentFee) {
      if (installmentFeePaidBy === 2) {
        return "Loja + cliente";
      }
      if (installmentFeePaidBy === 1) {
        return "Loja pagou";
      }
      return "Não informado";
    }

    if (hasInstallmentFee) {
      return getFeePayerLabel(installmentFeePaidBy);
    }

    return "Loja pagou";
  };

  const pageNumbers = useMemo(() => {
    const numbers: (number | string)[] = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        numbers.push(i);
      }
    } else {
      numbers.push(1);
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      if (start > 2) {
        numbers.push("...");
      }
      for (let i = start; i <= end; i++) {
        numbers.push(i);
      }
      if (end < totalPages - 1) {
        numbers.push("...");
      }
      numbers.push(totalPages);
    }

    return numbers;
  }, [page, totalPages]);

  const summary = report?.summary;
  const monthlyTotals = useMemo(() => {
    const grouped = new Map<string, number>();

    (report?.dailyTotals ?? []).forEach((point) => {
      const date = new Date(point.date);
      if (Number.isNaN(date.getTime())) {
        return;
      }
      const key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
      grouped.set(key, (grouped.get(key) ?? 0) + point.gross);
    });

    return Array.from(grouped.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, gross]) => ({
        date: `${key}-01`,
        gross,
        platform: 0,
        tenant: 0,
      }));
  }, [report]);

  return (
    <div
      className={`bg-gray-50 min-h-screen flex flex-col`}
    >
      <SmartNavbar />
      <div
        className={`p-4 sm:p-14 space-y-6 flex-1 flex flex-col`}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-4">
            <PageTitle>{"FATURAMENTO"}</PageTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-center">
              <div className="hidden lg:flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                  {([
                    { label: "Todos", value: undefined },
                    { label: "Online", value: OrderSource.Online },
                    { label: "Balcão", value: OrderSource.InStore },
                  ] as { label: string; value: OrderSource | undefined }[]).map(({ label, value }) => (
                    <button
                      key={label}
                      disabled={isLoading || isRefreshing}
                      onClick={() => { setOrderSource(value); loadReport(1, true, value ?? null); }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-60 ${
                        orderSource === value
                          ? "bg-primary-600 text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-center bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <FiCalendar />
                  <span>
                    {"Filtrar por data de criação"}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:items-center">
                  <input
                    type="date"
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <span className="text-gray-400 text-sm text-center">
                    {"até"}
                  </span>
                  <input
                    type="date"
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                  <Button
                    onClick={handleFilter}
                    disabled={isLoading || isRefreshing}
                    className="px-4 py-2 text-sm"
                  >
                    {"Aplicar"}
                  </Button>
                  <Button variant="secondary"
                    onClick={handleRefresh}
                    disabled={isLoading || isRefreshing}
                    className="px-3 py-2 text-sm"
                  >
                    {"Atualizar"}
                  </Button>
                  <Button isLoading={isGeneratingReport} variant="secondary"
                    onClick={handleOpenReportModal}
                    disabled={isGeneratingReport || isLoading || isRefreshing}
                    className="px-3 py-2 text-sm"
                  >
                    {"Exportar relatório"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {!isLoading && (
            <div className="lg:hidden flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm self-start">
              {([
                { label: "Todos", value: undefined },
                { label: "Online", value: OrderSource.Online },
                { label: "Balcão", value: OrderSource.InStore },
              ] as { label: string; value: OrderSource | undefined }[]).map(({ label, value }) => (
                <button
                  key={label}
                  onClick={() => { setOrderSource(value); loadReport(1, true, value ?? null); }}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                    orderSource === value
                      ? "bg-primary-600 text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="mt-2 space-y-4 w-full">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse bg-white border rounded-xl p-4 shadow-sm">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : !isLoading && report !== null && orders.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
            <FaBoxOpen className="text-3xl" />
            <p className="text-center text-sm sm:text-base">{"Nenhum pedido pago encontrado nesse filtro."}</p>
          </div>
        ) : (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard
            title={"Total em produtos"}
            value={formatPriceBRL(summary?.totalProducts ?? 0)}
            icon={<FiPieChart />}
            accent="bg-blue-500"
            isLoading={isLoading}
          />
          <StatCard
            title={"Total bruto recebido"}
            value={formatPriceBRL(summary?.totalGross ?? 0)}
            icon={<FiDollarSign />}
            accent="bg-slate-600"
            isLoading={isLoading}
          />
          <StatCard
            title={"Repasse para loja"}
            value={formatPriceBRL(summary?.totalTenant ?? 0)}
            icon={<FiTrendingUp />}
            accent="bg-emerald-500"
            isLoading={isLoading}
          />
          <StatCard
            title={"Receita de frete"}
            value={formatPriceBRL(summary?.totalShipping ?? 0)}
            icon={<FiTruck />}
            accent="bg-indigo-500"
            isLoading={isLoading}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <DailyLineChart
              points={monthlyTotals}
              isLoading={isLoading}
            />
          </div>
          <PaymentMethodBars
            methods={report?.paymentMethods ?? []}
            isLoading={isLoading}
          />
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                {"Pedidos pagos"}
              </h2>
              <p className="text-sm text-gray-500">
                {"Mostrando valores armazenados no pedido (comissão e frete)"}
              </p>
            </div>
            {(isLoading || isRefreshing) && (
              <div className="text-xs text-gray-500">
                {"Atualizando..."}
              </div>
            )}
          </div>
          {/* MOBILE CARDS */}
          <div className="sm:hidden divide-y divide-gray-100">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, idx) => (
                <div key={idx} className="p-4 space-y-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3" />
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
                </div>
              ))
            ) : orders.length === 0 ? (
              <p className="px-4 py-10 text-center text-gray-500 text-sm">
                {"Nenhum pedido pago encontrado nesse filtro."}
              </p>
            ) : (
              orders.map((order) => (
                <div key={order.orderId} className="p-4 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-800">#{order.orderNumber}</span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      {getPaymentStatusLabel(order.paymentStatus)}
                    </span>
                  </div>
                  <div className="text-gray-500 text-xs">{formatDateTimeToBR(order.paidAt)}</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-700 mt-1">
                    <div><span className="text-gray-400">{"Bruto"}:</span> {formatPriceBRL(order.grossAmount)}</div>
                    <div><span className="text-gray-400">{"Frete"}:</span> {order.shippingAmount > 0 ? formatPriceBRL(order.shippingAmount) : "-"}</div>
                    <div>
                      <span className="text-gray-400">{"Taxa"}:</span>{" "}
                      {getFeeAmount(order) > 0 ? formatPriceBRL(getFeeAmount(order)) : "-"}
                      {order.feeBreakdown?.totalFeeAmount ? (
                        <button type="button" className="ml-1 text-primary-600 hover:underline" onClick={() => setSelectedFeeOrder(order)}>
                          <FaInfoCircle className="inline text-[11px]" />
                        </button>
                      ) : null}
                    </div>
                    <div><span className="text-gray-400">{"Loja"}:</span> <span className="text-emerald-700 font-medium">{formatPriceBRL(order.tenantAmount)}</span></div>
                  </div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-2 px-2 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700">
                      {getPaymentMethodLabel(order.paymentMethod ?? null)}
                    </span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      order.orderSource === OrderSource.InStore
                        ? "bg-amber-100 text-amber-700"
                        : "bg-blue-100 text-blue-700"
                    }`}>
                      {getOrderSourceLabel(order.orderSource)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* DESKTOP TABLE */}
          <div className="hidden sm:block overflow-x-auto">
            <table
              className={`min-w-full text-sm`}
            >
              <thead className="bg-primary-120 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-3">
                    {"Pedido"}
                  </th>
                  <th className="text-left px-4 py-3">{"Tipo"}</th>
                  <th className="text-left px-4 py-3">{"Status"}</th>
                  <th className="text-center px-4 py-3">
                    {"Bruto"}
                  </th>
                  <th className="text-center px-4 py-3">
                    {"Frete"}
                  </th>
                  <th className="text-center px-4 py-3">{"Taxa"}</th>
                  <th className="text-center px-4 py-3">
                    {"Loja"}
                  </th>
                  <th className="text-left px-4 py-3">
                    {"Pagamento"}
                  </th>
                  <th className="text-left px-4 py-3">{"Pago em"}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      {Array.from({ length: 9 }).map((__, cidx) => (
                        <td key={cidx} className="px-4 py-4">
                          <div className="h-4 bg-gray-200 rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : orders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-10 text-center text-gray-500"
                    >
                      {"Nenhum pedido pago encontrado nesse filtro."}
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr
                      key={order.orderId}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-4 py-3 font-semibold text-gray-800">
                        #{order.orderNumber}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          order.orderSource === OrderSource.InStore
                            ? "bg-amber-100 text-amber-700"
                            : "bg-blue-100 text-blue-700"
                        }`}>
                          {getOrderSourceLabel(order.orderSource)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          {getPaymentStatusLabel(order.paymentStatus)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {formatPriceBRL(order.grossAmount)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {order.shippingAmount > 0 ? formatPriceBRL(order.shippingAmount) : "-"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center text-xs text-gray-700">
                          {getFeeAmount(order) > 0 ? (
                            <>
                              <span>{formatPriceBRL(getFeeAmount(order))}</span>
                              {getFeePayerSummary(order) ? (
                                <span className="text-[11px] text-gray-500">
                                  {getFeePayerSummary(order)}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <span>-</span>
                          )}
                          {order.feeBreakdown?.totalFeeAmount ? (
                            <button
                              type="button"
                              className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary-600 hover:underline"
                              onClick={() => setSelectedFeeOrder(order)}
                            >
                              <FaInfoCircle className="text-[12px]" />
                              {"Detalhes"}
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-emerald-700 font-medium">
                        {formatPriceBRL(order.tenantAmount)}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <span className="inline-flex items-center gap-2 px-2 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700">
                          {getPaymentMethodLabel(order.paymentMethod ?? null)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {formatDateTimeToBR(order.paidAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {orders.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-700 flex-col sm:flex-row gap-3">
              <div>
                {report?.orders.totalItems}{" "}
                {report?.orders.totalItems === 1
                  ? "pedido"
                  : "pedidos"}{" "}
                {"pagos"}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-2 border rounded-lg bg-white disabled:opacity-50"
                >
                  {"Anterior"}
                </button>
                {pageNumbers.map((p, idx) =>
                  typeof p === "string" ? (
                    <span key={idx} className="px-2 text-gray-500">
                      {p}
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`px-3 py-2 border rounded-lg ${p === page ? "bg-primary-600 text-white border-primary-600" : "bg-white"}`}
                    >
                      {p}
                    </button>
                  ),
                )}
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="px-3 py-2 border rounded-lg bg-white disabled:opacity-50"
                >
                  {"Próximo"}
                </button>
              </div>
            </div>
          )}
        </div>
        </>
        )}

        <ManageSubscriptionCard />

        <BillingFeeDetailsModal
          order={selectedFeeOrder}
          onClose={() => setSelectedFeeOrder(null)}
        />
        <ReportFormatModal
          isOpen={isReportFormatModalOpen}
          onClose={() => setIsReportFormatModalOpen(false)}
          onSelect={handleSelectReportFormat}
        />
      </div>
    </div>
  );
};

export default AdminBillingPage;
